import { combineReducers } from "@reduxjs/toolkit";
import { produce } from "immer";

import type { MapShareAction } from "domains/createShareSettings/actionTypes";
import { MapShareType } from "domains/createShareSettings/actionTypes";
import type {
  CollaboratorState,
  InvitationState,
  OpenLinkState,
  ShareLinkState,
} from "domains/createShareSettings/types";

const invitationInitialState: InvitationState = {
  isPending: false,
  error: false,
};

const invitation = (state = invitationInitialState, action: MapShareAction): InvitationState => {
  switch (action.type) {
    case MapShareType.INVITE: {
      return {
        ...state,
        isPending: true,
      };
    }
    case MapShareType.INVITE_FULFILLED: {
      return {
        ...state,
        isPending: false,
        error: false,
      };
    }
    case MapShareType.INVITE_REJECTED: {
      return {
        ...state,
        isPending: false,
        error: true,
      };
    }
    default:
      return state;
  }
};

const collaboratorsInitialState: CollaboratorState = {
  collaborators: [],
  error: false,
  invitees: [],
  isPending: false,
};

const collaborators = (state = collaboratorsInitialState, action: MapShareAction): CollaboratorState => {
  const update = (recipe: (draft: CollaboratorState) => void) => produce(state, recipe);
  switch (action.type) {
    case MapShareType.INVITE_FULFILLED:
      return update((draft) => {
        draft.invitees.push(
          ...action.payload.map((item) => ({
            ...item,
            first_name: null,
            full_name: null,
            id: null,
            image_url: null,
            last_name: null,
            pending: true,
          })),
        );
      });
    case MapShareType.ACCESS_LIST:
      return update((draft) => {
        draft.isPending = true;
      });
    case MapShareType.ACCESS_LIST_FULFILLED:
      return update((draft) => {
        draft.collaborators = [...action.payload.owners, ...action.payload.editors, ...action.payload.viewers];
        draft.error = false;
        draft.invitees = action.payload.invitees;
        draft.isPending = false;
      });
    case MapShareType.ACCESS_LIST_REJECTED:
      return update((draft) => {
        draft.error = true;
        draft.isPending = false;
      });
    case MapShareType.REMOVE_INVITE:
      return update((draft) => {
        const target = [...draft.collaborators, ...draft.invitees].find((item) => item.email === action.meta.email);
        if (target !== undefined) target.removePending = true;
      });
    case MapShareType.REMOVE_INVITE_FULFILLED:
      return update((draft) => {
        draft.collaborators = draft.collaborators.filter((item) => item.email !== action.meta.email);
        draft.invitees = draft.invitees.filter((item) => item.email !== action.meta.email);
      });
    case MapShareType.REMOVE_INVITE_REJECTED:
      return update((draft) => {
        const target = [...draft.collaborators, ...draft.invitees].find((item) => item.email === action.meta.email);
        if (target !== undefined) target.removePending = false;
      });
    case MapShareType.CHANGE_INVITE_PERMISSION:
      return update((draft) => {
        const target = [...draft.collaborators, ...draft.invitees].find((item) => item.email === action.meta.email);
        if (target !== undefined) target.isPermissionChanging = true;
      });
    case MapShareType.CHANGE_INVITE_PERMISSION_REJECTED:
    case MapShareType.CHANGE_INVITE_PERMISSION_FULFILLED:
      return update((draft) => {
        const target = [...draft.collaborators, ...draft.invitees].find((item) => item.email === action.meta.email);
        if (target !== undefined) target.isPermissionChanging = false;
      });
    default:
      return state;
  }
};

const shareLinkInitialState: ShareLinkState = {
  isPending: false,
};

const shareLink = (state = shareLinkInitialState, action: MapShareAction): ShareLinkState => {
  switch (action.type) {
    case MapShareType.CREATE_LINK:
      return {
        ...state,
        isPending: true,
      };
    case MapShareType.CREATE_LINK_FULFILLED:
      return {
        ...state,
        isPending: false,
        error: false,
        token: action.payload.invite_token,
      };
    case MapShareType.CREATE_LINK_REJECTED:
      return {
        ...state,
        error: true,
        isPending: false,
      };
    default:
      return state;
  }
};

const openLinkInitialState: OpenLinkState = {
  isPending: false,
};

const openLink = (state = openLinkInitialState, action: MapShareAction): OpenLinkState => {
  switch (action.type) {
    case MapShareType.OPEN_MAP_INVITATION:
      return {
        ...state,
        isPending: true,
      };
    case MapShareType.OPEN_MAP_INVITATION_FULFILLED:
      return {
        ...state,
        data: action.payload,
        errorStatus: undefined,
        isPending: false,
      };
    case MapShareType.OPEN_MAP_INVITATION_REJECTED:
      return {
        ...state,
        errorStatus: action.payload.errorStatus,
        isPending: false,
      };
    default:
      return state;
  }
};

export const shareMapReducer = combineReducers({
  invitation,
  collaborators,
  shareLink,
  openLink,
});
